import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { fetchRatioTrend } from '../../../services/analysisRatioTrend';
import { fetchStandardRatios } from '../../../services/analysisRatiosStandard';
import { exportRatioTrendPdf } from './ratioTrendPdfExport';
import './RatioTrendAnalysisPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'Trend Intelligence',
    description: 'CEO ve CFO icin donemsel rasyo momentumu, esik ihlalleri ve kritik hareketlerin ozeti.',
    loadingCalc: 'Hesaplaniyor...',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    calculate: 'Trend Hesapla',
    reportingBasis: 'Raporlama Bazi',
    ifrsDefault: 'IFRS (Varsayilan)',
    vukOption: 'VUK',
    activeBasis: 'Analiz Bazi',
    company: 'Sirket',
    startPeriod: 'Baslangic Donemi',
    endPeriod: 'Bitis Donemi',
    maxPeriod: 'Maksimum Donem',
    loadSelectionsError: 'Secimler yuklenemedi.',
    selectRange: 'Lutfen sirket ve donem araligi secin.',
    calcError: 'Trend hesaplanamadi.',
    noTrendData: 'Trend verisi yok.',
    chartAria: 'Rasyo trend grafigi',
    tooltipCompany: 'Sirket',
    tooltipStandard: 'Standart',
    tooltipDiff: 'Fark',
    trendScore: 'Trend Skoru',
    strong: 'Guclu',
    balanced: 'Dengeli',
    risky: 'Riskli',
    trendDirection: 'Trend Yonu',
    rising: 'Yukselen',
    falling: 'Dusen',
    flat: 'Yatay',
    volatility: 'Volatilite',
    volatilityNote: 'Periodik oynaklik seviyesi',
    thresholdCompliance: 'Esik Uyum Orani',
    periodViolation: '{{count}} donem ihlal',
    criticalMoves: 'Kritik Hareketler',
    criticalMovesSub: 'Baslangic -> Bitis en yuksek degisim',
    noCriticalMoves: 'Kritik hareket verisi yok.',
    trendOverlay: 'Dual-Line Trend + Esik Overlay',
    ratio: 'Rasyo',
    strengthening: 'Gucleniyor',
    weakening: 'Zayifliyor',
    executiveTable: 'Yonetici Tablosu',
    periodCount: '{{count}} donem',
    period: 'Donem',
    emptyTable: 'Trend verisi yok. Trend Hesapla butonunu kullanin.'
  },
  'en-US': {
    title: 'Trend Intelligence',
    description: 'Summary of periodic ratio momentum, threshold violations, and critical movements for CEOs and CFOs.',
    loadingCalc: 'Calculating...',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    calculate: 'Calculate Trend',
    reportingBasis: 'Reporting Basis',
    ifrsDefault: 'IFRS (Default)',
    vukOption: 'VUK',
    activeBasis: 'Analysis Basis',
    company: 'Company',
    startPeriod: 'Start Period',
    endPeriod: 'End Period',
    maxPeriod: 'Maximum Periods',
    loadSelectionsError: 'Selections could not be loaded.',
    selectRange: 'Please select company and period range.',
    calcError: 'Trend could not be calculated.',
    noTrendData: 'No trend data.',
    chartAria: 'Ratio trend chart',
    tooltipCompany: 'Company',
    tooltipStandard: 'Standard',
    tooltipDiff: 'Difference',
    trendScore: 'Trend Score',
    strong: 'Strong',
    balanced: 'Balanced',
    risky: 'Risky',
    trendDirection: 'Trend Direction',
    rising: 'Rising',
    falling: 'Falling',
    flat: 'Flat',
    volatility: 'Volatility',
    volatilityNote: 'Periodic volatility level',
    thresholdCompliance: 'Threshold Compliance',
    periodViolation: '{{count}} period violations',
    criticalMoves: 'Critical Moves',
    criticalMovesSub: 'Highest change from start to end',
    noCriticalMoves: 'No critical movement data.',
    trendOverlay: 'Dual-Line Trend + Threshold Overlay',
    ratio: 'Ratio',
    strengthening: 'Strengthening',
    weakening: 'Weakening',
    executiveTable: 'Executive Table',
    periodCount: '{{count}} periods',
    period: 'Period',
    emptyTable: 'No trend data. Use Calculate Trend button.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const RATIO_LABELS = {
  net_profit_margin: 'Satis Karliligi',
  asset_turnover: 'Aktif Devir Hizi',
  return_on_assets: 'ROA',
  working_capital_ratio: 'Isletme Sermayesi Orani',
  debt_ratio: 'Borc Yuku',
  debt_to_equity_ratio: 'Borc / Ozkaynak',
  return_on_equity: 'ROE',
  ebitda_margin: 'EBITDA Marji',
  working_capital_turnover: 'Isletme Sermayesi Devir Hizi'
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const TrendChart = ({ points, standard, violations, t, formatNumber, formatPercent }) => {
  if (!points.length) return <p className="rt-empty">{t('noTrendData')}</p>;

  const width = 760;
  const height = 250;
  const left = 40;
  const right = 22;
  const top = 18;
  const bottom = 42;
  const w = width - left - right;
  const h = height - top - bottom;

  const values = points.map((point) => point.value);
  const allValues = [...values, Number(standard)];
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = Math.max(max - min, 0.000001);

  const xOf = (index) => left + (points.length === 1 ? w / 2 : (index / (points.length - 1)) * w);
  const yOf = (value) => top + ((max - value) / range) * h;

  const companyPath = points.map((point, index) => `${xOf(index)},${yOf(point.value)}`).join(' ');
  const standardPath = points.map((_, index) => `${xOf(index)},${yOf(Number(standard))}`).join(' ');

  return (
    <div className="rt-chart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="rt-chart" role="img" aria-label={t('chartAria')}>
        <line x1={left} y1={top} x2={left} y2={top + h} className="rt-axis" />
        <line x1={left} y1={top + h} x2={left + w} y2={top + h} className="rt-axis" />
        <polyline points={standardPath} className="rt-standard-line" />
        <polyline points={companyPath} className="rt-company-line" />

        {points.map((point, index) => (
          <g key={`${point.periodCode}-${index}`}>
            <circle cx={xOf(index)} cy={yOf(point.value)} r="4" className={violations.has(point.periodCode) ? 'rt-point-violation' : 'rt-point'} />
            <title>{`${point.periodCode} | ${t('tooltipCompany')} ${formatNumber(point.value, 4)} | ${t('tooltipStandard')} ${formatNumber(standard, 4)} | ${t('tooltipDiff')} ${formatPercent(point.deviation, 2)}`}</title>
          </g>
        ))}

        {points.map((point, index) => (
          <text key={`lbl-${point.periodCode}`} x={xOf(index)} y={height - 12} textAnchor="middle" className="rt-x-label">{point.periodCode}</text>
        ))}
      </svg>
    </div>
  );
};

function RatioTrendAnalysisPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const formatNumber = useCallback((value, digits = 2) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return number.toLocaleString(localeKey, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  }, [localeKey]);

  const formatSigned = useCallback((value, digits = 2) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return `${number > 0 ? '+' : ''}${number.toLocaleString(localeKey, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  }, [localeKey]);

  const formatPercent = useCallback((value, digits = 1) => {
    const number = Number(value);
    if (Number.isNaN(number)) return '-';
    return `${number.toLocaleString(localeKey, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
  }, [localeKey]);

  const [companies, setCompanies] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [basis, setBasis] = useState('IFRS');
  const [selectedStartPeriodId, setSelectedStartPeriodId] = useState('');
  const [selectedEndPeriodId, setSelectedEndPeriodId] = useState('');
  const [maxPeriods, setMaxPeriods] = useState(12);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [trend, setTrend] = useState(null);
  const [standardItems, setStandardItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [message, setMessage] = useState('');
  const pageCaptureRef = useRef(null);

  useEffect(() => {
    const loadSelections = async () => {
      try {
        const [companiesResponse, periodsResponse] = await Promise.all([
          fetch(`${API_BASE}/api/companies`, { headers: { 'Content-Type': 'application/json', ...(sessionStorage.getItem('authToken') ? { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` } : {}) } }),
          fetch(`${API_BASE}/api/periods`, { headers: { 'Content-Type': 'application/json', ...(sessionStorage.getItem('authToken') ? { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` } : {}) } })
        ]);

        if (!companiesResponse.ok || !periodsResponse.ok) throw new Error(t('loadSelectionsError'));

        const [companyData, periodData] = await Promise.all([companiesResponse.json(), periodsResponse.json()]);
        setCompanies(companyData || []);
        setPeriods(periodData || []);

        if (companyData?.length) setSelectedCompanyId(String(companyData[0].id));
        if (periodData?.length) {
          const ordered = [...periodData].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
          setSelectedStartPeriodId(String(ordered[0].id));
          setSelectedEndPeriodId(String(ordered[ordered.length - 1].id));
        }
      } catch (error) {
        setMessage(error instanceof Error ? error.message : t('loadSelectionsError'));
      }
    };

    loadSelections();
  }, [t]);

  const hasSelection = Boolean(selectedCompanyId && selectedStartPeriodId && selectedEndPeriodId);

  const orderedPeriods = useMemo(() => [...periods].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)), [periods]);
  const selectedStartDate = useMemo(() => orderedPeriods.find((period) => String(period.id) === selectedStartPeriodId)?.startDate, [orderedPeriods, selectedStartPeriodId]);
  const selectedEndDate = useMemo(() => orderedPeriods.find((period) => String(period.id) === selectedEndPeriodId)?.startDate, [orderedPeriods, selectedEndPeriodId]);

  const filteredStartPeriods = useMemo(() => {
    if (!selectedEndDate) return orderedPeriods;
    return orderedPeriods.filter((period) => new Date(period.startDate) <= new Date(selectedEndDate));
  }, [orderedPeriods, selectedEndDate]);

  const filteredEndPeriods = useMemo(() => {
    if (!selectedStartDate) return orderedPeriods;
    return orderedPeriods.filter((period) => new Date(period.startDate) >= new Date(selectedStartDate));
  }, [orderedPeriods, selectedStartDate]);

  const handleCalculate = async () => {
    if (!hasSelection) {
      setMessage(t('selectRange'));
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const trendResult = await fetchRatioTrend(selectedCompanyId, selectedStartPeriodId, selectedEndPeriodId, maxPeriods, basis);
      const activePeriodId = trendResult?.items?.length > 0 ? String(trendResult.items[trendResult.items.length - 1].periodId) : selectedEndPeriodId;

      const standardResult = await fetchStandardRatios(selectedCompanyId, activePeriodId, basis);
      const standards = standardResult?.items || [];

      setStandardItems(standards);
      setTrend(trendResult);
      setSelectedMetric((prev) => prev || standards[0]?.key || '');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('calcError'));
      setTrend(null);
      setStandardItems([]);
    } finally {
      setLoading(false);
    }
  };

  const standardMap = useMemo(() => {
    const map = new Map();
    standardItems.forEach((item) => map.set(item.key, item));
    return map;
  }, [standardItems]);

  const ratioOrder = useMemo(() => standardItems.map((item) => item.key), [standardItems]);
  const selectedRatio = standardMap.get(selectedMetric) || standardItems[0] || null;

  const trendSummary = useMemo(() => {
    if (!trend?.items?.length || trend.items.length < 2) return { up: 0, down: 0, flat: 0 };

    const first = trend.items[0].metrics || {};
    const last = trend.items[trend.items.length - 1].metrics || {};
    let up = 0;
    let down = 0;
    let flat = 0;

    ratioOrder.forEach((key) => {
      const start = first[key];
      const end = last[key];
      if (start == null || end == null) return;
      const delta = Number(end) - Number(start);
      if (Math.abs(delta) < 0.0001) flat += 1;
      else if (delta > 0) up += 1;
      else down += 1;
    });

    return { up, down, flat };
  }, [trend, ratioOrder]);

  const trendPoints = useMemo(() => {
    if (!trend?.items?.length || !selectedRatio?.key) return [];

    const standard = Number(selectedRatio.standardValue || 0);
    return trend.items
      .map((item) => {
        const value = Number(item.metrics?.[selectedRatio.key]);
        if (Number.isNaN(value)) return null;
        return {
          periodCode: item.periodCode,
          value,
          deviation: standard === 0 ? 0 : ((value - standard) / Math.abs(standard)) * 100
        };
      })
      .filter(Boolean);
  }, [trend, selectedRatio]);

  const violations = useMemo(() => {
    const set = new Set();
    if (!selectedRatio || !trendPoints.length) return set;

    const standard = Number(selectedRatio.standardValue || 0);
    trendPoints.forEach((point) => {
      const violation = selectedRatio.higherIsBetter === false ? point.value > standard : point.value < standard;
      if (violation) set.add(point.periodCode);
    });
    return set;
  }, [selectedRatio, trendPoints]);

  const trendDirection = useMemo(() => {
    if (trendPoints.length < 2) return t('flat');
    const start = trendPoints[0].value;
    const end = trendPoints[trendPoints.length - 1].value;
    if (Math.abs(end - start) < 0.0001) return t('flat');
    return end > start ? t('strengthening') : t('weakening');
  }, [trendPoints, t]);

  const volatility = useMemo(() => {
    if (trendPoints.length < 2) return 0;
    const deltas = [];
    for (let index = 1; index < trendPoints.length; index += 1) {
      deltas.push(Math.abs(trendPoints[index].value - trendPoints[index - 1].value));
    }
    return deltas.reduce((acc, item) => acc + item, 0) / deltas.length;
  }, [trendPoints]);

  const coverage = trendPoints.length > 0 ? clamp(((trendPoints.length - violations.size) / trendPoints.length) * 100, 0, 100) : 0;
  const trendScore = clamp((trendSummary.up * 8) - (trendSummary.down * 7) - (violations.size * 6) + 55, 0, 100);

  const criticalMovers = useMemo(() => {
    if (!trend?.items?.length || trend.items.length < 2) return [];
    const first = trend.items[0].metrics || {};
    const last = trend.items[trend.items.length - 1].metrics || {};

    return ratioOrder
      .map((key) => {
        const start = Number(first[key]);
        const end = Number(last[key]);
        if (Number.isNaN(start) || Number.isNaN(end)) return null;
        const delta = end - start;
        return { key, title: RATIO_LABELS[key] || key, start, end, delta, capped: clamp(delta, -2.5, 2.5) };
      })
      .filter(Boolean)
      .sort((left, right) => Math.abs(right.delta) - Math.abs(left.delta))
      .slice(0, 5);
  }, [trend, ratioOrder]);

  const selectedCompany = useMemo(
    () => companies.find((item) => String(item.id) === String(selectedCompanyId)) || null,
    [companies, selectedCompanyId]
  );
  const selectedStartPeriod = useMemo(
    () => periods.find((item) => String(item.id) === String(selectedStartPeriodId)) || null,
    [periods, selectedStartPeriodId]
  );
  const selectedEndPeriod = useMemo(
    () => periods.find((item) => String(item.id) === String(selectedEndPeriodId)) || null,
    [periods, selectedEndPeriodId]
  );

  const handleExportPdf = useCallback(async () => {
    if (!pageCaptureRef.current || exportingPdf) {
      return;
    }
    try {
      setExportingPdf(true);
      await exportRatioTrendPdf({
        element: pageCaptureRef.current,
        companyName: selectedCompany?.name || '',
        periodLabel: `${selectedStartPeriod?.code || '-'} - ${selectedEndPeriod?.code || '-'}`,
        language: localeKey
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('calcError'));
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, localeKey, selectedCompany, selectedStartPeriod, selectedEndPeriod, t]);

  return (
    <div className="card ratio-trend-card" ref={pageCaptureRef}>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={
          <div className="ratio-trend-page-actions" data-export-hidden="true">
            <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={loading || exportingPdf || !hasSelection}>
              {exportingPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
            <button type="button" className="btn-primary" onClick={handleCalculate} disabled={loading || exportingPdf || !hasSelection}>
              {loading ? t('loadingCalc') : t('calculate')}
            </button>
          </div>
        }
      />

      <section className="ratio-trend-controls">
        <label>{t('company')}
          <select value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)}>
            {companies.map((company) => (<option key={company.id} value={company.id}>{company.name}</option>))}
          </select>
        </label>
        <label>{t('reportingBasis')}
          <select value={basis} onChange={(event) => setBasis(event.target.value)}>
            <option value="IFRS">{t('ifrsDefault')}</option>
            <option value="VUK">{t('vukOption')}</option>
          </select>
        </label>
        <label>{t('startPeriod')}
          <select value={selectedStartPeriodId} onChange={(event) => setSelectedStartPeriodId(event.target.value)}>
            {filteredStartPeriods.map((period) => (<option key={period.id} value={period.id}>{period.code}</option>))}
          </select>
        </label>
        <label>{t('endPeriod')}
          <select value={selectedEndPeriodId} onChange={(event) => setSelectedEndPeriodId(event.target.value)}>
            {filteredEndPeriods.map((period) => (<option key={period.id} value={period.id}>{period.code}</option>))}
          </select>
        </label>
        <label>{t('maxPeriod')}
          <input type="number" min={1} max={60} value={maxPeriods} onChange={(event) => setMaxPeriods(Number(event.target.value) || 12)} />
        </label>
      </section>

      {message && <p className="ratio-trend-message">{message}</p>}
      {!message && trend?.reportingBasis && <p className="ratio-trend-message">{t('activeBasis')}: {trend.reportingBasis}</p>}

      <section className="rt-executive-grid">
        <article className="rt-exec-card rt-gauge-card">
          <header><span>{t('trendScore')}</span><strong>{formatNumber(trendScore, 0)}</strong></header>
          <div className="rt-gauge" aria-hidden="true"><div className="rt-gauge-center">{formatNumber(trendScore, 0)}</div></div>
          <footer><span className={`rt-badge ${trendScore >= 75 ? 'positive' : trendScore >= 50 ? 'warn' : 'negative'}`}>{trendScore >= 75 ? t('strong') : trendScore >= 50 ? t('balanced') : t('risky')}</span></footer>
        </article>

        <article className="rt-exec-card">
          <header><span>{t('trendDirection')}</span><strong>{trendDirection}</strong></header>
          <div className="rt-kpi-row"><span>{t('rising')}</span><b>{trendSummary.up}</b></div>
          <div className="rt-kpi-row"><span>{t('falling')}</span><b>{trendSummary.down}</b></div>
          <div className="rt-kpi-row"><span>{t('flat')}</span><b>{trendSummary.flat}</b></div>
        </article>

        <article className="rt-exec-card">
          <header><span>{t('volatility')}</span><strong>{formatNumber(volatility, 3)}</strong></header>
          <div className="rt-progress"><span style={{ width: `${clamp(volatility * 40, 0, 100)}%` }} /></div>
          <footer><span>{t('volatilityNote')}</span></footer>
        </article>

        <article className="rt-exec-card">
          <header><span>{t('thresholdCompliance')}</span><strong>{formatPercent(coverage, 0)}</strong></header>
          <div className="rt-progress"><span style={{ width: `${coverage}%` }} /></div>
          <footer><span>{t('periodViolation').replace('{{count}}', String(violations.size))}</span></footer>
        </article>
      </section>

      <section className="rt-section">
        <div className="rt-section-head"><h3>{t('criticalMoves')}</h3><span>{t('criticalMovesSub')}</span></div>
        <div className="rt-diverging-list">
          {!criticalMovers.length ? <p className="rt-empty">{t('noCriticalMoves')}</p> : criticalMovers.map((item) => (
            <article key={item.key} className="rt-diverging-row">
              <div className="rt-diverging-head"><strong>{item.title}</strong><span>{formatSigned(item.delta, 3)}</span></div>
              <div className="rt-diverging-track"><span className="rt-diverging-center" /><span className={`rt-diverging-bar ${item.capped >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(item.capped) * 20)}%`, [item.capped >= 0 ? 'left' : 'right']: '50%' }} /></div>
            </article>
          ))}
        </div>
      </section>

      <section className="rt-section">
        <div className="rt-section-head">
          <h3>{t('trendOverlay')}</h3>
          <div className="rt-inline-control">
            <label htmlFor="rt-metric-select">{t('ratio')}</label>
            <select id="rt-metric-select" value={selectedRatio?.key || ''} onChange={(event) => setSelectedMetric(event.target.value)}>
              {ratioOrder.map((key) => <option key={key} value={key}>{RATIO_LABELS[key] || key}</option>)}
            </select>
          </div>
        </div>

        <TrendChart points={trendPoints} standard={selectedRatio?.standardValue || 0} violations={violations} t={t} formatNumber={formatNumber} formatPercent={formatPercent} />

        <div className="rt-trend-footer">
          <span className={`rt-badge ${trendDirection === t('strengthening') ? 'positive' : trendDirection === t('weakening') ? 'negative' : 'neutral'}`}>
            {trendDirection === t('strengthening') ? `^ ${t('strengthening')}` : trendDirection === t('weakening') ? `v ${t('weakening')}` : `› ${t('flat')}`}
          </span>
          <span>{trend?.items?.[0]?.periodCode || '-'} - {trend?.items?.[trend?.items?.length - 1]?.periodCode || '-'}</span>
        </div>
      </section>

      <section className="rt-section">
        <div className="rt-section-head"><h3>{t('executiveTable')}</h3><span>{t('periodCount').replace('{{count}}', String(trend?.items?.length || 0))}</span></div>
        <div className="ratio-trend-table-wrapper">
          <table className="ratio-trend-table">
            <thead>
              <tr>
                <th>{t('period')}</th>
                {ratioOrder.map((key) => <th key={key}>{RATIO_LABELS[key] || key}</th>)}
              </tr>
            </thead>
            <tbody>
              {!trend?.items?.length ? (
                <tr><td colSpan={ratioOrder.length + 1} className="ratio-trend-empty">{loading ? t('loadingCalc') : t('emptyTable')}</td></tr>
              ) : (
                trend.items.map((item, index) => {
                  const previous = index > 0 ? trend.items[index - 1] : null;
                  return (
                    <tr key={`${item.periodId}-${item.periodCode}`}>
                      <td><strong>{item.periodCode}</strong></td>
                      {ratioOrder.map((key) => {
                        const value = item.metrics?.[key];
                        const prev = previous?.metrics?.[key];
                        const delta = value != null && prev != null ? Number(value) - Number(prev) : null;
                        return (
                          <td key={`${item.periodId}-${key}`}>
                            <div className="ratio-trend-value">{formatNumber(value, 4)}</div>
                            <div className={`ratio-trend-delta ${delta > 0 ? 'pos' : delta < 0 ? 'neg' : 'neu'}`}>{previous ? formatSigned(delta, 3) : '-'}</div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default RatioTrendAnalysisPage;
